/**
 * JWT Token utility functions
 * Provides safe token parsing and expiration checking
 */

/**
 * Safely decode JWT payload without verification
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export const decodeTokenPayload = (token: string): any | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // JWT payload is base64url encoded, convert it to standard base64 first
    let payload = parts[1];
    payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if missing
    while (payload.length % 4 !== 0) {
      payload += '=';
    }

    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if a JWT token is expired
 * @param token - JWT token string or null
 * @returns true if token is expired or invalid, false if valid
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) {
    return true;
  }

  try {
    const payload = decodeTokenPayload(token);
    if (!payload || !payload.exp) {
      return true; // If no expiration, consider expired for safety
    }

    // exp is in seconds, convert to milliseconds
    const expirationTime = payload.exp * 1000;
    const now = Date.now();
    
    // Add 60 second buffer to account for clock skew and network delays
    return expirationTime < (now + 60000);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // If we can't parse, consider expired
  }
};

/**
 * Get the expiration time of a JWT token
 * @param token - JWT token string
 * @returns Expiration timestamp in milliseconds or null if invalid
 */
export const getTokenExpirationTime = (token: string): number | null => {
  try {
    const payload = decodeTokenPayload(token);
    if (!payload || !payload.exp) {
      return null;
    }
    // exp is in seconds, convert to milliseconds
    return payload.exp * 1000;
  } catch (error) {
    console.error('Error getting token expiration time:', error);
    return null;
  }
};

/**
 * Get time until token expires in milliseconds
 * @param token - JWT token string
 * @returns Milliseconds until expiration, or null if invalid/expired
 */
export const getTimeUntilExpiration = (token: string): number | null => {
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) {
    return null;
  }
  
  const now = Date.now();
  const timeUntilExpiration = expirationTime - now;
  
  return timeUntilExpiration > 0 ? timeUntilExpiration : null;
};
